import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Modal, ModalHeader, ModalBody, Spinner, Card, CardBody, Button } from 'reactstrap';
import documentsApi, { inferFilename } from 'service/documentsApi';
import DocumentDownloadButton from './DocumentDownloadButton';
import DocViewer, { DocViewerRenderers } from 'react-doc-viewer';
import { GlobalWorkerOptions } from 'pdfjs-dist/build/pdf';

const isImageType = (ct) => /^image\//i.test(ct || '');

const DocumentPreview = ({ documentId, variant = 'modal', height = 520, className, autoFetchDetails = true, isOpen = true, onClose }) => {
  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(!!autoFetchDetails);
  const [error, setError] = useState(null);
  const [resourceUrl, setResourceUrl] = useState(null); // ObjectURL para inline
  const [resourceBlob, setResourceBlob] = useState(null); // Blob crudo para librerías de preview
  const [previewDirectUrl, setPreviewDirectUrl] = useState(null); // URL directa del backend (presigned)
  const [downloadDirectUrl, setDownloadDirectUrl] = useState(null); // URL presignada de descarga (sin headers)
  const [contentType, setContentType] = useState(null);
  const [inlineBlocked, setInlineBlocked] = useState(false); // CORS/headers impiden inline
  const [textContent, setTextContent] = useState(null); // Para previsualizar CSV/Texto
  const objectUrlRef = useRef(null);
  const [fileExt, setFileExt] = useState(null); // Extensión detectada (para Office)
  const docxContainerRef = useRef(null);
  const xlsxContainerRef = useRef(null);

  const canInline = useMemo(() => {
    const ct = contentType || details?.contentType;
    if (!ct) return false;
    return ct === 'application/pdf' || isImageType(ct);
  }, [contentType, details]);

  useEffect(() => {
    // Configurar el worker de PDF.js desde CDN para evitar fallos por import dinámico
    try {
      if (!GlobalWorkerOptions.workerSrc) {
        GlobalWorkerOptions.workerSrc = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js';
      }
    } catch (_) {}

    let cancelled = false;
    const load = async () => {
      setError(null);
      try {
        if (autoFetchDetails) {
          setLoading(true);
          const d = await documentsApi.getDocumentDetails(documentId);
          if (cancelled) return;
          setDetails(d);
        }

        // Intentar usar recurso de previsualización; si CORS no permite inline, obtener blob
        const p = await documentsApi.getPreviewResource(documentId);
        if (p && p.url) setPreviewDirectUrl(p.url);
        if (cancelled) return;
        if (p.url) {
          // Para evitar descargas automáticas por Content-Disposition=attachment,
          // intentamos obtener blob y renderizar con ObjectURL
          try {
            const resp = await fetch(p.url);
            if (cancelled) return;
            const blob = await resp.blob();
            const obj = URL.createObjectURL(blob);
            objectUrlRef.current = obj;
            setResourceUrl(obj);
            setResourceBlob(blob);
            const respCt = resp.headers.get('content-type') || '';
            setContentType(respCt || blob.type || details?.contentType || null);
            // Intentar deducir extensión desde Content-Disposition o URL
            try {
              const cd = resp.headers.get('Content-Disposition') || resp.headers.get('content-disposition');
              let ext = null;
              if (cd) {
                const m = /filename\*?=(?:UTF-8''|\")?([^";]+)/i.exec(cd);
                if (m && m[1]) {
                  const fname = decodeURIComponent(m[1]);
                  const idx = fname.lastIndexOf('.');
                  if (idx > -1) ext = fname.substring(idx + 1).toLowerCase();
                }
              }
              if (!ext && p.url) {
                const path = new URL(p.url).pathname;
                const i = path.lastIndexOf('.');
                if (i > -1) ext = path.substring(i + 1).toLowerCase();
              }
              if (ext) setFileExt(ext);
            } catch (_) {}
            try {
              const lower = (respCt || blob.type || '').toLowerCase();
              if (lower.startsWith('text/') || lower.includes('csv')) {
                const txt = await blob.text();
                if (!cancelled) setTextContent(txt);
              } else {
                setTextContent(null);
              }
            } catch (_) { setTextContent(null); }
          } catch (_) {
            // Si CORS u otro error impide fetch, NO usar URL directa para evitar descarga automática
            setInlineBlocked(true);
            setResourceUrl(null);
            setResourceBlob(null);
            setContentType(details?.contentType || null);
            // Deducir extensión desde URL directa
            try {
              if (p.url) {
                const path = new URL(p.url).pathname;
                const i = path.lastIndexOf('.');
                if (i > -1) setFileExt(path.substring(i + 1).toLowerCase());
              }
            } catch (_) {}
          }
        } else if (p.blob) {
          const obj = URL.createObjectURL(p.blob);
          objectUrlRef.current = obj;
          setResourceUrl(obj);
          setResourceBlob(p.blob);
          setContentType(p.contentType);
          try {
            // Intentar deducir extensión desde contentType
            const ctMap = {
              'application/msword': 'doc',
              'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'docx',
              'application/vnd.ms-excel': 'xls',
              'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'xlsx',
              'application/vnd.ms-powerpoint': 'ppt',
              'application/vnd.openxmlformats-officedocument.presentationml.presentation': 'pptx',
            };
            const ext = ctMap[String(p.contentType || '').toLowerCase()];
            if (ext) setFileExt(ext);
          } catch (_) {}
          try {
            const lower = (p.contentType || '').toLowerCase();
            if (lower.startsWith('text/') || lower.includes('csv')) {
              const txt = await p.blob.text();
              if (!cancelled) setTextContent(txt);
            } else {
              setTextContent(null);
            }
          } catch (_) { setTextContent(null); }
        }

        // Independientemente del tipo de preview, obtener URL de descarga presignada
        try {
          const d = await documentsApi.getPresignedDownloadUrl(documentId).catch(() => null);
          if (!cancelled && d && d.url) setDownloadDirectUrl(d.url);
        } catch (_) {}
      } catch (e) {
        if (!cancelled) setError(e?.message || 'Error cargando el documento.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    if (documentId) load();
    return () => {
      cancelled = true;
      if (objectUrlRef.current) {
        URL.revokeObjectURL(objectUrlRef.current);
        objectUrlRef.current = null;
      }
      setResourceUrl(null);
      setTextContent(null);
    };
  }, [documentId, autoFetchDetails]);

  // Fallback render: DOCX → docx-preview (CDN) | XLS/XLSX → xlsx to HTML (CDN)
  useEffect(() => {
    const ct = (contentType || '').toLowerCase();
    const ext = (fileExt || '').toLowerCase();
    const isWord = (!!resourceBlob) && (ct.includes('application/msword') || ct.includes('application/vnd.openxmlformats-officedocument.wordprocessingml.document') || ext === 'doc' || ext === 'docx');
    const isExcel = (!!resourceBlob) && (ct.includes('application/vnd.ms-excel') || ct.includes('application/vnd.openxmlformats-officedocument.spreadsheetml.sheet') || ext === 'xls' || ext === 'xlsx');

    let cancelled = false;

    const renderDocx = async () => {
      if (!docxContainerRef.current) return;
      // Limpiar contenedor
      docxContainerRef.current.innerHTML = '';
      try {
        const mod = await import(/* webpackIgnore: true */ 'https://cdn.jsdelivr.net/npm/docx-preview@0.3.1/dist/docx-preview.es.js');
        if (cancelled) return;
        const renderAsync = mod.renderAsync || mod.default?.renderAsync || mod?.renderAsync;
        if (typeof renderAsync === 'function') {
          await renderAsync(resourceBlob, docxContainerRef.current, { inWrapper: true, useMathML: true, className: 'docx-preview' });
        }
      } catch (_) {
        // silencioso: si falla, el contenido() hará fallback a Office viewer o descarga
      }
    };

    const renderXlsx = async () => {
      if (!xlsxContainerRef.current) return;
      xlsxContainerRef.current.innerHTML = '';
      try {
        const XLSX = await import(/* webpackIgnore: true */ 'https://cdn.jsdelivr.net/npm/xlsx@0.18.5/+esm');
        if (cancelled) return;
        const buf = await resourceBlob.arrayBuffer();
        const wb = XLSX.read(buf, { type: 'array' });
        const sheetName = wb.SheetNames[0];
        const sheet = wb.Sheets[sheetName];
        const html = XLSX.utils.sheet_to_html(sheet, { header: `<h6 style="margin:4px 0">${sheetName}</h6>` });
        xlsxContainerRef.current.innerHTML = html;
      } catch (_) {
        // silencioso
      }
    };

    if (isWord) {
      renderDocx();
    } else if (isExcel) {
      renderXlsx();
    }

    return () => {
      cancelled = true;
      if (docxContainerRef.current) docxContainerRef.current.innerHTML = '';
      if (xlsxContainerRef.current) xlsxContainerRef.current.innerHTML = '';
    };
  }, [resourceBlob, contentType, fileExt]);

  const content = () => {
    if (loading) return (<div className="text-center py-5"><Spinner color="primary" /></div>);
    if (error) return (<div className="text-center text-danger py-4">{error}</div>);
    // Detectar tipos Office por content-type o extensión
    const ct = (contentType || '').toLowerCase();
    const ext = (fileExt || '').toLowerCase();
    const isWord = ct.includes('application/msword') || ct.includes('application/vnd.openxmlformats-officedocument.wordprocessingml.document') || ext === 'doc' || ext === 'docx';
    const isExcel = ct.includes('application/vnd.ms-excel') || ct.includes('application/vnd.openxmlformats-officedocument.spreadsheetml.sheet') || ext === 'xls' || ext === 'xlsx';
    const isPpt = ct.includes('application/vnd.ms-powerpoint') || ct.includes('application/vnd.openxmlformats-officedocument.presentationml.presentation') || ext === 'ppt' || ext === 'pptx';

    // Intentar renderizar en-app si tenemos el blob (mejor UX y evita depender de terceros)
    // Prefer Office Online Viewer (suele permitir más orígenes embebidos que Google Docs)
    if ((isWord || isExcel || isPpt) && (previewDirectUrl || downloadDirectUrl)) {
      const src = encodeURIComponent(previewDirectUrl || downloadDirectUrl);
      const office = `https://view.officeapps.live.com/op/view.aspx?src=${src}`;
      const gdocs = `https://docs.google.com/gview?embedded=1&url=${src}`;
      return (
        <div style={{ width: '100%', height }}>
          <div className="d-flex justify-content-end mb-2">
            <Button color="link" size="sm" className="p-0 mr-2" onClick={() => window.open(office, '_blank')}>Open in Office</Button>
            <Button color="link" size="sm" className="p-0" onClick={() => window.open(gdocs, '_blank')}>Open in Google</Button>
          </div>
          <iframe title={`Preview ${documentId}`} src={office} style={{ width: '100%', height: height - 28 }} />
        </div>
      );
    }

    if (isWord && resourceBlob) {
      return (
        <div style={{ width: '100%', height, overflow: 'auto', border: '1px solid #e9ecef', borderRadius: 4, background: '#fff' }}>
          <div ref={docxContainerRef} style={{ padding: 12 }} />
        </div>
      );
    }
    if (isExcel && resourceBlob) {
      return (
        <div style={{ width: '100%', height, overflow: 'auto', border: '1px solid #e9ecef', borderRadius: 4, background: '#fff' }}>
          <style>{`.xlsx-html table{border-collapse:collapse;width:100%} .xlsx-html td,.xlsx-html th{border:1px solid #e1e3e8;padding:4px 6px;font-size:.85rem}`}</style>
          <div ref={xlsxContainerRef} className="xlsx-html" style={{ padding: 12 }} />
        </div>
      );
    }

    // Si aún no se pudo, intentar Office Online como último recurso (usa preview o download URL)
    if ((isWord || isExcel || isPpt) && (previewDirectUrl || downloadDirectUrl || resourceUrl)) {
      const office = `https://view.officeapps.live.com/op/view.aspx?src=${encodeURIComponent(previewDirectUrl || downloadDirectUrl || resourceUrl)}`;
      return (
        <iframe title={`Preview ${documentId}`} src={office} style={{ width: '100%', height }} />
      );
    }

    if (!resourceUrl || inlineBlocked) {
      // Fallback: tarjeta con metadata y botón de descarga
      return (
        <Card className="border-0">
          <CardBody className="text-center">
            <div className="mb-3">
              <i className="ni ni-single-copy-04 text-muted" style={{ fontSize: '3rem' }} />
            </div>
            <div className="mb-2">{inferFilename(details, documentId, contentType || details?.contentType)}</div>
            <div className="text-muted mb-3">{contentType || details?.contentType || 'application/octet-stream'}</div>
            <DocumentDownloadButton documentId={documentId} details={details} />
          </CardBody>
        </Card>
      );
    }
    // Caso especial: PDF → usar iframe directo para evitar import dinámico del worker
    if ((contentType || '').includes('application/pdf')) {
      return (
        <iframe title={`Preview ${documentId}`} src={resourceUrl} style={{ width: '100%', height }} />
      );
    }

    // Fallback para Word/Excel/PowerPoint: usar Office Online Viewer
    if (isWord || isExcel || isPpt) {
      // Si pudimos obtener la URL directa (presigned), usar Office Online Viewer
      const srcUrl = previewDirectUrl || resourceUrl;
      const office = `https://view.officeapps.live.com/op/view.aspx?src=${encodeURIComponent(srcUrl)}`;
      return (
        <iframe title={`Preview ${documentId}`} src={office} style={{ width: '100%', height }} />
      );
    }

    // Caso especial: CSV o texto → renderizar como texto monoespaciado con scroll
    const ctLower = (contentType || '').toLowerCase();
    if (ctLower.includes('text/csv') || ctLower.startsWith('text/plain')) {
      return (
        <div style={{ width: '100%', height, overflow: 'auto', background: '#fafafa', border: '1px solid #e9ecef', borderRadius: 4 }}>
          <pre style={{ whiteSpace: 'pre', margin: 0, padding: '12px', fontFamily: 'Courier, monospace', fontSize: '0.85rem' }}>
            {textContent || 'Loading...'}
          </pre>
        </div>
      );
    }

    // Usar react-doc-viewer para múltiples tipos (imágenes, etc.)
    const docs = [{ uri: resourceUrl, fileType: contentType || (details?.contentType) }];
    return (
      <div style={{ width: '100%', height }}>
        <DocViewer
          documents={docs}
          pluginRenderers={DocViewerRenderers}
          style={{ width: '100%', height: '100%' }}
          config={{ header: { retainURLParams: true } }}
        />
      </div>
    );
  };

  if (variant === 'modal') {
    return (
      <Modal isOpen={isOpen} toggle={onClose} size="xl" className={className}>
        <ModalHeader toggle={onClose}>Document Preview</ModalHeader>
        <ModalBody>
          {content()}
        </ModalBody>
      </Modal>
    );
  }

  // sidepanel u otro contenedor inline
  return (
    <div className={className}>
      {content()}
    </div>
  );
};

export default DocumentPreview;



